export const GARDEN_MOODS = ['mist', 'autumn', 'moon'] as const;
export type GardenMood = typeof GARDEN_MOODS[number];
export const DEFAULT_MOOD: GardenMood = 'autumn';
export type GardenChoice = GardenMood | 'auto';
export const DEFAULT_GARDEN_CHOICE: GardenChoice = 'auto';
export function gardenChoice(value: string | null): GardenChoice {
  return value === 'auto' || GARDEN_MOODS.includes(value as GardenMood) ? value as GardenChoice : DEFAULT_GARDEN_CHOICE;
}
/** Local device time only; no location, room message or telemetry. */
export function moodForHour(hour: number): GardenMood {
  return hour >= 5 && hour < 11 ? 'mist' : hour >= 11 && hour < 18 ? 'autumn' : 'moon';
}
export function nextMoodBoundary(now: Date): number {
  const next = new Date(now);
  const hour = now.getHours();
  next.setHours(hour < 5 ? 5 : hour < 11 ? 11 : hour < 18 ? 18 : 29, 0, 0, 0);
  return next.getTime();
}
export const DEFAULT_FLOOR_OPACITY = 0.18;
export function floorOpacity(value: string | null): number {
  if (value === null || value.trim() === '') return DEFAULT_FLOOR_OPACITY;
  const opacity = Number(value);
  return Number.isFinite(opacity) && opacity >= 0 && opacity <= 1 ? opacity : DEFAULT_FLOOR_OPACITY;
}
export const landscapePath = (mood: GardenMood) => GARDEN_BACKGROUNDS[mood].landscape;
export const portraitLandscapePath = (mood: GardenMood) => GARDEN_BACKGROUNDS[mood].portrait;
export function gardenMood(value: string | null): GardenMood {
  return GARDEN_MOODS.includes(value as GardenMood) ? value as GardenMood : DEFAULT_MOOD;
}
export const GARDEN_PALETTES = {
  mist: { floor: 0xd8d3bd, edge: 0xa8ab96, seam: 0xb8b8a2, platform: 0xd2c7a9, pond: 0x8ba9aa, plants: 0xe6edcf, statue: 0xf4f0dc, ornament: 0xe2ebd6, leaf: 0xadb783, fall: [0xadb783, 0xc8c27a, 0xd8c37f, 0x9fae78] },
  autumn: { floor: 0xd6bd98, edge: 0x9e8970, seam: 0xb49b7c, platform: 0xd7bd92, pond: 0x899b91, plants: 0xefca97, statue: 0xffe2ad, ornament: 0xf1d5ad, leaf: 0xd5a16c, fall: [0xd5a16c, 0xe0b765, 0xc98e55, 0xb9b06a] },
  moon: { floor: 0x687b85, edge: 0x354958, seam: 0x89999c, platform: 0x87949b, pond: 0x4e7183, plants: 0x879b9b, statue: 0xd8e5e6, ornament: 0xa3bdc5, leaf: 0x92aab0, fall: [0x92aab0, 0xa7b5a6, 0xb3b49a, 0x8aa0a4] },
} as const;

/** Background records are separate from locale, room geometry and device location (§7.3).
 * Only the three already approved skies are selectable; no new destinations are enabled. */
export const GARDEN_BACKGROUNDS = Object.fromEntries(GARDEN_MOODS.map(id => [id, {
  id, nameKey: `personal.moods.${id}`, country: null, region: null, city: null,
  landscape: `/art/landscapes/${id}-v${id === 'moon' ? 3 : 2}.webp`, portrait: `/art/landscapes/${id}-portrait-v2.webp`,
  portraitWidths: [768, 941, 1296, 1536], palette: GARDEN_PALETTES[id],
  source: 'image_gen.imagegen', license: 'Project-generated illustration; provenance in ASSETS.md',
  provenance: ['docs/design/world-v2/provenance.json', ...(id === 'moon' ? ['docs/design/moon-axis-fix/provenance.json'] : [])],
}])) as Record<GardenMood, { id: GardenMood; nameKey: string; country: string | null; region: string | null; city: string | null; landscape: string; portrait: string; portraitWidths: number[]; palette: typeof GARDEN_PALETTES[GardenMood]; source: string; license: string; provenance: string[] }>;
